import { Product } from "~/domains/product/Product";
import { type CartElement } from "./CartElement";
import { useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import { Card } from "~/features/configurator/Linear/components/Card";
import { IconButton, styled, Typography } from "~/common/mui";
import { C_PRIMARY } from "~/common/components/icon/Colors";
import { useState } from "react";
import { t } from "i18next";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

const PRODUCT_SIZE_S = "196px";
const PRODUCT_SIZE_M = "228px";
const CMT_SIZE_S = "416px";

const Header = styled("div")(({ theme }) => ({
  display: "grid",
  margin: `${theme.spacing(2)} 0`,
  gridTemplateColumns: `${PRODUCT_SIZE_S} ${PRODUCT_SIZE_S} ${CMT_SIZE_S}`,
  columnGap: theme.spacing(2.5),
  gridTemplateAreas: '"prod qty cmt"',
  borderBottom: `1px solid ${theme.palette.divider}`,
  paddingBottom: theme.spacing(4),
  "&:last-child": {
    border: "none",
  },
}));

const MobileHeader = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  display: "flex",
}));

const MobileContainer = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: `${PRODUCT_SIZE_M} auto`,
  gridTemplateRows: "repeat(2, auto)",
  gridColumnGap: 0,
  gridRowGap: theme.spacing(1),
  gridTemplateAreas: '"prod qty" "cmt cmt"',
}));

const ProductArea = styled("div")(({ theme }) => ({
  gridArea: "prod",
  display: "flex",
  flexFlow: "column",
  gap: theme.spacing(2),
}));

const QuantityArea = styled("div")(({ theme }) => ({
  gridArea: "qty",
  "& > p": {
    color: theme.palette.text.secondary,
  },
}));

const CommentArea = styled("div")(({ theme }) => ({
  gridArea: "cmt",
  display: "flex",
  justifyContent: "space-between",
  "& div > p": {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  "& p": {
    margin: 0,
    marginTop: theme.spacing(0.375),
  },
}));

const Designation = styled(Typography)(({ theme }) => ({
  ...theme.typography.titleMedium,
  fontWeight: "bold",
}));

const ProductInfo = styled("div")(({ theme }) => ({
  display: "flex",
  flexFlow: "column",
  gap: theme.spacing(1),
}));

interface B2CRequestCartItemProps {
  item: CartElement;
  className?: string;
}

const B2CRequestCartItem = ({ item, className }: B2CRequestCartItemProps) => {
  const { data, product } = item;
  const [isOpen, setOpen] = useState(true);

  const isTablet = useIsMinTabletSize();

  const desktop = (
    <Header className={className}>
      <ProductArea>
        <Product.PrimaryImageWithLogos />
        <ProductInfo>
          <div className="d-flex">
            {product.preferred && <Product.PreferredIndicator className={"icon-margin"} />}
            <Designation variant="titleMedium">
              <Product.Designation />
            </Designation>
          </div>
          <Product.MMDTag />
          <Product.ShortDescription clickable={false} />
        </ProductInfo>
      </ProductArea>
      <QuantityArea>{data.quantity}</QuantityArea>
      <CommentArea>{data.comment}</CommentArea>
    </Header>
  );

  const mobile = (
    <MobileHeader disableHover>
      <MobileContainer>
        <div>
          <div>
            <Product.PrimaryImageWithLogos size="tiny" />
          </div>

          <ProductInfo className="mt-16">
            <div className="d-flex">
              {product.preferred && <Product.PreferredIndicator className={"icon-margin"} />}
              <Designation variant="titleMedium">
                <Product.Designation />
              </Designation>
            </div>

            <Product.ShortDescription clickable={false} />
          </ProductInfo>
        </div>
        <div className="d-flex flex-justify-end">
          <QuantityArea>
            <p>{t("cartPage.table.header.quantity.title")}</p>
            <span>{data.quantity}</span>
          </QuantityArea>
        </div>
        {data.comment && (
          <CommentArea>
            <div>
              <p>{t("cartPage.table.comment")}</p>
              {isOpen && <span>{data.comment}</span>}
            </div>
            <div>
              <IconButton color="primary" type="button" size="small" onClick={() => setOpen(!isOpen)}>
                {isOpen ? (
                  <KeyboardArrowUpIcon sx={{ color: C_PRIMARY, fontSize: 16 }} />
                ) : (
                  <KeyboardArrowDownIcon sx={{ color: C_PRIMARY, fontSize: 16 }} />
                )}
              </IconButton>
            </div>
          </CommentArea>
        )}
      </MobileContainer>
    </MobileHeader>
  );

  return <Product.Context value={product}>{isTablet ? desktop : mobile}</Product.Context>;
};

export default B2CRequestCartItem;
